import React, { useEffect } from "react";

import { X } from "lucide-react";
import { createPortal } from "react-dom";

import Button from "../Button";

import "./style.scss";
interface CommonModalProps {
  isOpen: boolean;
  toggle: () => void;
  children: React.ReactNode;
  size?: string;
  centered?: boolean;
  showHeader?: boolean;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  footer?: React.ReactNode;
  onCloseButton?: () => void;
  className?: string;
  overlayClassName?: string;
  containerClassName?: string;
  /** Use e.g. "overflow-hidden flex flex-col" for fixed header/footer with scrollable body */
  contentClassName?: string;
  /** Rendered in the header to the left of the close button */
  headerActions?: React.ReactNode;
  /** Render at document.body so the overlay covers the full app (sidebar included) */
  usePortal?: boolean;
  /** Raise z-index and portal so this modal stacks above another open modal */
  nested?: boolean;
}

const CommonModal: React.FC<CommonModalProps> = ({
  isOpen,
  toggle,
  children,
  size = "md",
  centered = true,
  showHeader = true,
  title,
  subtitle,
  footer,
  onCloseButton,
  className,
  overlayClassName,
  containerClassName,
  contentClassName,
  headerActions,
  usePortal = false,
  nested = false,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
    "6xl": "max-w-6xl",
    "7xl": "max-w-7xl",
    full: "max-w-full",
    "screen-sm": "max-w-screen-sm",
    "screen-md": "max-w-screen-md",
    "screen-lg": "max-w-screen-lg",
    "screen-xl": "max-w-screen-xl",
    "screen-2xl": "max-w-screen-2xl",
  };

  const overlayClasses = overlayClassName ?? "bg-black/50";
  const overlayZIndex = nested ? "z-[1060]" : "z-[1040]";
  const containerZIndex = nested ? "z-[1070]" : "z-[1050]";
  const shouldPortal = usePortal || nested;
  const modalMarkup = (
    <>
      <div
        className={`fixed inset-0 ${overlayZIndex} ${overlayClasses}`}
        onClick={toggle}
        aria-hidden="true"
      />
      <div
        className={`fixed inset-0 ${containerZIndex} flex ${centered ? "items-center justify-center" : "items-start justify-center pt-16"} p-4 ${containerClassName || ""}`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div
          className={`bg-white rounded-lg shadow-lg w-full ${sizeClasses[size as keyof typeof sizeClasses] || sizeClasses["3xl"]} max-h-[90vh] overflow-hidden flex flex-col add-account-modal ${className || ""}`}
        >
          {showHeader ? (
            <div className="common-modal__header flex justify-between items-start gap-3 p-5 border-b border-gray-200">
              <div className="min-w-0 flex-1 pr-2">
                {title ? (
                  <h5 className="common-modal__title share-modal__header mb-0 font-semibold text-gray-900 leading-snug">
                    {title}
                  </h5>
                ) : null}
                {subtitle ? (
                  <div className="common-modal__subtitle mb-0 mt-1.5 leading-relaxed">
                    {subtitle}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {headerActions}
                <Button
                  variant="clear"
                  onClick={onCloseButton || toggle}
                  className="share-modal__close-btn shrink-0"
                  aria-label="Close"
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
          ) : null}
          <div
            className={`common-modal__body p-5 flex-1 min-h-0 ${contentClassName ?? "overflow-y-auto"}`}
          >
            {children}
          </div>
          {footer ? (
            <div className="common-modal__footer p-4 px-5 border-t border-gray-200 flex-shrink-0 bg-white flex flex-wrap items-center justify-end gap-2">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </>
  );

  return shouldPortal ? createPortal(modalMarkup, document.body) : modalMarkup;
};

export default CommonModal;
